<template>
  <el-container class="default-layout">
    <el-aside :width="isCollapsed ? '64px' : '220px'" class="sidebar">
      <div class="logo">
        <template v-if="!isCollapsed">
          <img src="/bajar_logo.png" alt="Bajar" class="logo-image" />
          <span class="logo-subtitle">Admin</span>
        </template>
        <img v-else src="/bajar_logo.png" alt="Bajar" class="logo-image-collapsed" />
      </div>

      <el-menu
        :default-active="activeMenu"
        :collapse="isCollapsed"
        :collapse-transition="false"
        router
        class="sidebar-menu"
      >
        <el-menu-item index="/dashboard">
          <el-icon><HomeFilled /></el-icon>
          <template #title>Dashboard</template>
        </el-menu-item>

        <el-menu-item index="/users">
          <el-icon><User /></el-icon>
          <template #title>Users</template>
        </el-menu-item>

        <el-menu-item index="/tasks">
          <el-icon><Document /></el-icon>
          <template #title>Tasks</template>
        </el-menu-item>

        <el-menu-item index="/focus-sessions">
          <el-icon><Timer /></el-icon>
          <template #title>Focus Sessions</template>
        </el-menu-item>

        <el-menu-item index="/gamification">
          <el-icon><Trophy /></el-icon>
          <template #title>Gamification</template>
        </el-menu-item>

        <el-menu-item index="/subscriptions">
          <el-icon><Star /></el-icon>
          <template #title>Subscriptions</template>
        </el-menu-item>

        <el-menu-item index="/transactions">
          <el-icon><Wallet /></el-icon>
          <template #title>Transactions</template>
        </el-menu-item>

        <el-menu-item index="/notifications">
          <el-icon><Bell /></el-icon>
          <template #title>Notifications</template>
        </el-menu-item>

        <el-menu-item index="/analytics">
          <el-icon><TrendCharts /></el-icon>
          <template #title>Analytics</template>
        </el-menu-item>

        <el-menu-item index="/audit-log">
          <el-icon><List /></el-icon>
          <template #title>Audit Log</template>
        </el-menu-item>

        <el-menu-item index="/ai-keys">
          <el-icon><Key /></el-icon>
          <template #title>AI API Keys</template>
        </el-menu-item>

        <el-menu-item index="/settings">
          <el-icon><Setting /></el-icon>
          <template #title>Settings</template>
        </el-menu-item>
      </el-menu>

      <div class="sidebar-footer">
        <el-button
          :icon="isCollapsed ? Expand : Fold"
          text
          class="collapse-btn"
          @click="toggleCollapse"
        />
      </div>
    </el-aside>

    <el-container class="main-container">
      <el-header class="header">
        <div class="header-left">
          <el-breadcrumb separator="/">
            <el-breadcrumb-item :to="{ path: '/dashboard' }">Home</el-breadcrumb-item>
            <el-breadcrumb-item v-for="(crumb, index) in breadcrumbs" :key="index">
              {{ crumb }}
            </el-breadcrumb-item>
          </el-breadcrumb>
        </div>

        <div class="header-right">
          <el-button
            :icon="themeStore.isDark ? Sunny : Moon"
            circle
            @click="themeStore.toggleTheme"
            title="Toggle theme"
          />

          <el-badge :value="notificationCount" :hidden="notificationCount === 0" class="notification-badge">
            <el-button :icon="Bell" circle />
          </el-badge>

          <el-dropdown trigger="click" @command="handleCommand">
            <span class="user-dropdown">
              <el-avatar :size="36" :src="user?.avatar">
                {{ user?.name?.[0]?.toUpperCase() || 'A' }}
              </el-avatar>
              <div class="user-info">
                <span class="user-name">{{ user?.name || 'Admin' }}</span>
                <span class="user-role">{{ user?.getDisplayRole?.() || 'Administrator' }}</span>
              </div>
              <el-icon><ArrowDown /></el-icon>
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="profile">
                  <el-icon><User /></el-icon> My Profile
                </el-dropdown-item>
                <el-dropdown-item command="settings">
                  <el-icon><Setting /></el-icon> Settings
                </el-dropdown-item>
                <el-dropdown-item command="logout" divided>
                  <el-icon><SwitchButton /></el-icon> Logout
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </el-header>

      <el-main class="main-content">
        <router-view v-slot="{ Component }">
          <transition name="fade" mode="out-in">
            <component :is="Component" />
          </transition>
        </router-view>
      </el-main>
    </el-container>
  </el-container>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/features/authentication/presentation/stores/auth.store'
import { useThemeStore } from '@/features/settings/presentation/stores/theme.store'
import {
  HomeFilled,
  User,
  Document,
  Star,
  TrendCharts,
  Setting,
  Bell,
  ArrowDown,
  SwitchButton,
  Fold,
  Expand,
  Timer,
  Trophy,
  List,
  Key,
  Sunny,
  Moon,
  Wallet,
} from '@element-plus/icons-vue'
import { ElMessageBox } from 'element-plus'

const themeStore = useThemeStore()

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const isCollapsed = ref(false)
const notificationCount = ref(3)

const activeMenu = computed(() => {
  const path = route.path
  // Handle nested routes by returning parent path
  if (path.startsWith('/users')) return '/users'
  if (path.startsWith('/tasks')) return '/tasks'
  if (path.startsWith('/focus-sessions')) return '/focus-sessions'
  if (path.startsWith('/gamification')) return '/gamification'
  if (path.startsWith('/notifications')) return '/notifications'
  if (path.startsWith('/audit-log')) return '/audit-log'
  return path
})

const user = computed(() => authStore.user)

const breadcrumbs = computed(() => {
  const matched = route.matched
  const crumbs: string[] = []

  matched.forEach((record) => {
    if (record.meta?.title && record.path !== '/') {
      crumbs.push(record.meta.title as string)
    }
  })

  return crumbs
})

function toggleCollapse() {
  isCollapsed.value = !isCollapsed.value
}

async function handleCommand(command: string) {
  switch (command) {
    case 'profile':
      router.push({ name: 'settings' })
      break
    case 'settings':
      router.push({ name: 'settings' })
      break
    case 'logout':
      try {
        await ElMessageBox.confirm('Are you sure you want to logout?', 'Logout', {
          type: 'warning',
        })
        await authStore.logout()
        router.push({ name: 'login' })
      } catch {
        // User cancelled
      }
      break
  }
}
</script>

<style scoped lang="scss">
.default-layout {
  min-height: 100vh;
}

.sidebar {
  background: var(--bg-primary);
  border-right: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  transition: width 0.3s, background-color 0.3s, border-color 0.3s;
  overflow: hidden;

  .logo {
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    border-bottom: 1px solid var(--border-color);
    flex-shrink: 0;

    .logo-image {
      width: 40px;
      height: 40px;
      object-fit: contain;
    }

    .logo-image-collapsed {
      width: 32px;
      height: 32px;
      object-fit: contain;
    }

    .logo-subtitle {
      font-size: 10px;
      color: var(--text-tertiary);
      text-transform: uppercase;
      letter-spacing: 2px;
    }
  }

  .sidebar-menu {
    flex: 1;
    border-right: none;
    overflow-y: auto;

    &:not(.el-menu--collapse) {
      width: 100%;
    }
  }

  .sidebar-footer {
    padding: 12px;
    border-top: 1px solid var(--border-color);
    flex-shrink: 0;

    .collapse-btn {
      width: 100%;
    }
  }
}

.main-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.header {
  background: var(--bg-primary);
  border-bottom: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  height: 64px;
  transition: background-color 0.3s, border-color 0.3s;

  .header-left {
    display: flex;
    align-items: center;
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 16px;

    .notification-badge {
      :deep(.el-badge__content) {
        top: 8px;
        right: 12px;
      }
    }

    .user-dropdown {
      display: flex;
      align-items: center;
      gap: 12px;
      cursor: pointer;
      padding: 4px 8px;
      border-radius: 8px;
      transition: background-color 0.2s;

      &:hover {
        background-color: var(--bg-hover);
      }

      .user-info {
        display: flex;
        flex-direction: column;
        align-items: flex-start;

        .user-name {
          font-weight: 500;
          font-size: 14px;
          color: var(--text-primary);
        }

        .user-role {
          font-size: 11px;
          color: var(--text-tertiary);
        }
      }
    }
  }
}

.main-content {
  background-color: var(--bg-secondary);
  padding: 0;
  min-height: calc(100vh - 64px);
  overflow-y: auto;
  transition: background-color 0.3s;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
